import { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, doc, updateDoc, deleteDoc, query, onSnapshot, orderBy, Timestamp } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { errorMessage } from '../helpers';

const normalizeEvents = (events = []) => {
    return events.map(event => {
        const eventDate = event.eventDate instanceof Date
            ? event.eventDate
            : event.eventDate instanceof Timestamp
                ? event.eventDate.toDate()
                : new Date(event.eventDate);
        return { ...event, eventDate };
    });
};

export const useTimeline = (userId, isDemoMode, showModal, demoState, t, currentSnapshotId = null, activeSnapshot = null, updateSnapshot = null) => {
    const [timelineEvents, setTimelineEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (currentSnapshotId && activeSnapshot) {
            setTimelineEvents(normalizeEvents(activeSnapshot.timelineEvents || []));
            setLoading(false);
            return;
        }
        if (isDemoMode) {
            setTimelineEvents(normalizeEvents(demoState.demoTimelineEvents || []));
            setLoading(false);
            return;
        }
        if (!userId) {
            setTimelineEvents([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const eventsCollectionPath = `artifacts/${appId}/users/${userId}/timelineEvents`;
        const qEvents = query(collection(db, eventsCollectionPath), orderBy('eventDate', 'asc'));
        
        const unsubscribe = onSnapshot(qEvents, (querySnapshot) => {
            const events = querySnapshot.docs.map(d => {
                const data = d.data();
                return { 
                    id: d.id, 
                    ...data,
                    eventDate: data.eventDate instanceof Timestamp ? data.eventDate.toDate() : new Date(data.eventDate),
                    isDisabled: data.isDisabled || false,
                };
            });
            setTimelineEvents(normalizeEvents(events));
            setLoading(false);
        }, (error) => {
            console.error("Error fetching timeline events:", error);
            showModal(errorMessage(t, error));
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId, isDemoMode, showModal, demoState.demoTimelineEvents, t, currentSnapshotId, activeSnapshot]);

    const persistSnapshotEvents = useCallback((newEvents) => {
        const normalized = normalizeEvents(newEvents);
        setTimelineEvents(normalized);
        if (currentSnapshotId && typeof updateSnapshot === 'function') {
            updateSnapshot(currentSnapshotId, { timelineEvents: normalized });
        }
    }, [currentSnapshotId, updateSnapshot]);

    // D2 — the demo's own store; `timelineEvents` is derived from it by the
    // effect above, so a write here propagates like a Firestore listener would.
    const writeDemoEvents = useCallback((updater) => {
        demoState.setDemoTimelineEvents(updater);
    }, [demoState]);

    // D2 — see usePortfolio: a snapshot stays read-only in the demo, because
    // "edit this historical record, but only in memory" means nothing.
    const blockedBySnapshot = useCallback(() => {
        if (isDemoMode && currentSnapshotId) {
            showModal(t('warnings.demoSnapshotReadOnly'));
            return true;
        }
        return false;
    }, [isDemoMode, currentSnapshotId, showModal, t]);

    const addTimelineEvent = useCallback(async (eventData, callbacks) => {
        if (blockedBySnapshot()) return;
        if (isDemoMode) {
            writeDemoEvents(prev => [...(prev || []), {
                ...eventData,
                id: eventData.id || `demo-event-${Date.now()}`,
                createdAt: new Date(),
                eventDate: eventData.eventDate instanceof Date ? eventData.eventDate : new Date(eventData.eventDate),
                isDisabled: false,
            }]);
            if (callbacks && callbacks.onSuccess) callbacks.onSuccess();
            return;
        }
        if (currentSnapshotId) {
            const newEvent = {
                ...eventData,
                id: eventData.id || `snap-event-${Date.now()}`,
                createdAt: Timestamp.now(),
                eventDate: eventData.eventDate instanceof Date ? eventData.eventDate : new Date(eventData.eventDate),
                isDisabled: false,
            };
            persistSnapshotEvents([...(timelineEvents || []), newEvent]);
            if (callbacks && callbacks.onSuccess) callbacks.onSuccess();
            return;
        }
        if (!userId) { return; }
        
        try {
            const collectionRef = collection(db, `artifacts/${appId}/users/${userId}/timelineEvents`);
            await addDoc(collectionRef, {
                ...eventData,
                isDisabled: false,
                userId: userId,
                createdAt: Timestamp.now()
            });
            if (callbacks && callbacks.onSuccess) callbacks.onSuccess();
        } catch (error) {
            console.error("Error adding timeline event: ", error);
            showModal(errorMessage(t, error));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotEvents, timelineEvents, blockedBySnapshot, writeDemoEvents]);

    const updateTimelineEvent = useCallback(async (eventId, eventData, callbacks) => {
        if (blockedBySnapshot()) return;
        if (isDemoMode) {
            // The event's own name is now the visitor's, so it stops following
            // the UI language — same rule as a renamed portfolio item.
            writeDemoEvents(prev => (prev || []).map(e => {
                if (e.id !== eventId) return e;
                const next = { ...e, ...eventData, updatedAt: new Date() };
                if (next.eventName !== e.eventName) delete next.eventNameKey;
                return next;
            }));
            if (callbacks && callbacks.onSuccess) callbacks.onSuccess();
            return;
        }
        if (currentSnapshotId) {
            const updated = (timelineEvents || []).map(e => e.id === eventId ? { ...e, ...eventData, updatedAt: Timestamp.now() } : e);
            persistSnapshotEvents(updated);
            if (callbacks && callbacks.onSuccess) callbacks.onSuccess();
            return;
        }
        if (!userId || !eventId) { showModal(t('warnings.missingEventId')); return; }
        
        try {
            const eventRef = doc(db, `artifacts/${appId}/users/${userId}/timelineEvents`, eventId);
            await updateDoc(eventRef, {
                ...eventData,
                updatedAt: Timestamp.now()
            });
            if (callbacks && callbacks.onSuccess) callbacks.onSuccess();
        } catch (error) {
            console.error("Error updating timeline event: ", error);
            showModal(errorMessage(t, error));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotEvents, timelineEvents, blockedBySnapshot, writeDemoEvents]);
    
    const toggleTimelineEventDisabled = useCallback(async (eventId, isDisabled) => {
        if (blockedBySnapshot()) return;
        if (isDemoMode) {
            writeDemoEvents(prev => (prev || []).map(e => e.id === eventId ? { ...e, isDisabled } : e));
            return;
        }
        if (currentSnapshotId) {
            const updated = (timelineEvents || []).map(e => e.id === eventId ? { ...e, isDisabled } : e);
            persistSnapshotEvents(updated);
            return;
        }
        if (!userId) { return; }
        
        try {
            const eventRef = doc(db, `artifacts/${appId}/users/${userId}/timelineEvents`, eventId);
            await updateDoc(eventRef, { isDisabled });
        } catch (error) {
            console.error("Error toggling timeline event: ", error);
            showModal(errorMessage(t, error));
        }
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotEvents, timelineEvents, blockedBySnapshot, writeDemoEvents]);


    const deleteTimelineEvent = useCallback(async (eventId) => {
        if (blockedBySnapshot()) return;
        const askThen = (run) => showModal(t('warnings.deleteEventConfirmation'), true, run, null, null, 'general.delete', 'general.cancel', true);
        if (isDemoMode) {
            askThen(() => writeDemoEvents(prev => (prev || []).filter(e => e.id !== eventId)));
            return;
        }
        if (currentSnapshotId) {
            askThen(() => persistSnapshotEvents((timelineEvents || []).filter(e => e.id !== eventId)));
            return;
        }
        if (!userId || !eventId) { showModal(t('warnings.missingEventId')); return; }

        askThen(async () => {
            try {
                const eventRef = doc(db, `artifacts/${appId}/users/${userId}/timelineEvents`, eventId);
                await deleteDoc(eventRef);
            } catch (error) {
                console.error("Error deleting timeline event: ", error);
                showModal(errorMessage(t, error));
            }
        });
    }, [userId, isDemoMode, showModal, t, currentSnapshotId, persistSnapshotEvents, timelineEvents, blockedBySnapshot, writeDemoEvents]);

    return { timelineEvents, loading, addTimelineEvent, updateTimelineEvent, deleteTimelineEvent, toggleTimelineEventDisabled };
};